/**
 * Centralized backend configuration.
 *
 * The browser never uses the AWS SDK directly — it talks to an API Gateway
 * stage that mints S3 presigned URLs. These values define that contract and
 * must stay wire-compatible with the deployed Lambda (see server/lambda/handler.js).
 */

export const AWS_REGION = 'ap-south-1';

/** API Gateway base URL. Overridable via VITE_API_BASE_URL for local/staging. */
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  'https://f3mzbuwej0.execute-api.ap-south-1.amazonaws.com/prod';

/**
 * Google OAuth Client ID for "Sign in with Google" (public by design — ships in
 * the bundle). The Lambda verifies tokens against this same audience.
 */
export const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ??
  '692007573136-t9qrvfqi924kjt379077pca3ilft86tu.apps.googleusercontent.com';

/**
 * CloudFront CDN base URL for serving objects directly.
 *
 * DISABLED (empty) now that access is per-event: serving images straight from
 * the CDN would bypass the Lambda's permission checks, letting any member open
 * any photo by URL. With this empty, image/sidecar GETs go through the
 * authorized /get-download-url endpoint, which enforces per-event access.
 * (Set VITE_CDN_BASE_URL to re-enable only if access control is removed.)
 */
export const CDN_BASE_URL = import.meta.env.VITE_CDN_BASE_URL ?? '';

export const API_ENDPOINTS = {
  UPLOAD_URL: `${API_BASE_URL}/get-upload-url`,
  DOWNLOAD_URL: `${API_BASE_URL}/get-download-url`,
  LIST_FILES: `${API_BASE_URL}/list-files`,
  SHARE_LINKS: `${API_BASE_URL}/share-links`,
  BULK_OPERATIONS: `${API_BASE_URL}/bulk-operations`,
  ME: `${API_BASE_URL}/me`,
  EVENTS: `${API_BASE_URL}/events`,
  MAP_POINTS: `${API_BASE_URL}/map-points`,
  REQUEST_ACCESS: `${API_BASE_URL}/request-access`,
  DOWNLOAD_ARCHIVE: `${API_BASE_URL}/download-archive`,
  ADMIN_USERS: `${API_BASE_URL}/admin/users`,
  ADMIN_USER: `${API_BASE_URL}/admin/user`,
  ADMIN_ACCESS: `${API_BASE_URL}/admin/access`,
  ADMIN_REQUESTS: `${API_BASE_URL}/admin/requests`,
} as const;

export const S3_CONFIG = {
  BUCKET_NAME: import.meta.env.VITE_S3_BUCKET_NAME ?? 'bhandari-photos-bucket',
  /** Originals live under this prefix. */
  UPLOAD_PREFIX: 'uploads',
  /** Thumbnails mirror originals under this prefix. */
  THUMBNAIL_PREFIX: 'thumb_uploads',
} as const;

export const UPLOAD_CONFIG = {
  /** Hard client cap (the S3 PUT itself is not size-checked server-side). */
  MAX_FILE_SIZE: 100 * 1024 * 1024, // 100 MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  /** Browsers report .mov as either video/mp4, video/quicktime, or "". */
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/quicktime', 'video/webm', 'video/ogg'],
  /** Compress images larger than this before upload. */
  COMPRESSION_THRESHOLD: 2 * 1024 * 1024, // 2 MB
  /** Max images to compress down to (longest edge). */
  COMPRESSION_MAX_EDGE: 2560,
  COMPRESSION_QUALITY: 0.82,
  /** Thumbnail box (longest edge fits within). */
  THUMBNAIL_MAX_EDGE: 400,
  THUMBNAIL_QUALITY: 0.8,
  /** Concurrent uploads in the queue. */
  CONCURRENCY: 4,
} as const;

export const PRESIGNED_URL_CONFIG = {
  /** Server signs URLs for this TTL; client cache expires slightly earlier. */
  EXPIRES_IN_SECONDS: 3600,
} as const;

/** Event whose photos have no explicit folder (legacy flat keys). */
export const UNCATEGORIZED_EVENT = 'Uncategorized';
